import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { useStoresUI } from '@context/StoresContext';
import { usePdp } from '@context/PdpContext';
import useDeliveryStockLevel from '@utils/delivery/useDeliveryStockLevel';
import SelectVariantMessage from './SelectVariantMessage';
import SelectStoreMessage from './SelectStoreMessage';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';

import { StorefrontIcon, TruckIcon } from '@phosphor-icons/react';
import StoreAvailabilityMessage from './StoreAvailabilityMessage';
import FulfillmentStockLevelRow from './FulfillmentStockLevelRow';

import { getPDPShippingOptions } from '@utils/shipping/getPDPShippingOptions';
import { ShippingRestrictions } from '@adapters/bigcommerce/types/product';
import { useLocale } from '@hooks/useLocale';

import styles from './ProductFulfillment.module.scss';

const ProductFulfillment = () => {
  const { t } = useTranslation('pdp');
  const locale = useLocale();
  const {
    selectedVariant,
    colours,
    sizes,
    shippingRestrictions,
    selectedAvailability,
  } = usePdp();

  const { favouriteStoreDetails } = useStoresUI();
  const isClient = useHasMounted();
  const stockLevelResult = useDeliveryStockLevel();
  const { stockLevel, stockLevelMessage } = stockLevelResult;
  const shippingOption =
    getPDPShippingOptions(
      shippingRestrictions as ShippingRestrictions,
      locale ?? 'en'
    ) ?? null;

  const isDisabled = !favouriteStoreDetails || !selectedVariant;

  return (
    <div className={styles.fulfillmentMethodsContainer}>
      {selectedAvailability && (
        <div className={styles.fulfillmentMethods}>
          <div className={styles.fulfillmentOptionContent}>
            <div className={styles.fulfillmentOptionHeader}>
              <TruckIcon
                width={20}
                height={20}
                className={cn(styles.fulfillmentOptionHeaderIcon, {
                  [styles.fullfillmentOptionDisabled]: !selectedVariant,
                })}
              />
              <span
                className={cn(styles.fulfillmentOptionTitle, {
                  [styles.fullfillmentOptionDisabled]: !selectedVariant,
                })}
              >
                {t('product.shipping.message.new')}
              </span>
            </div>
            <div
              className={cn(
                styles.fulfillmentOptionFlexContainer,
                styles.shippingFulfillmentOptionFlexContainer
              )}
            >
              {!selectedVariant && (
                <SelectVariantMessage
                  colours={colours?.length}
                  sizes={sizes?.length}
                  isAddress
                />
              )}
              <span className={styles.fulfillmentOptionDescription}>
                {selectedVariant &&
                  t('product.shipping.message.delivery.time', {
                    deliveryTime: shippingOption?.estimatedDeliveryDate,
                  })}
              </span>

              <div className={styles.stockLevelRowContainer}>
                <FulfillmentStockLevelRow
                  stockLevel={stockLevel}
                  stockLevelMessage={stockLevelMessage}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={styles.fulfillmentMethods}>
        <div className={styles.fulfillmentOptionContent}>
          {!selectedAvailability && (
            <span
              className={cn(styles.inStoreOnlyMessage, {
                [styles.inStoreOnlyMessageDisabled]: isDisabled,
              })}
            >
              {t('product.free.store.pickup.new.inStoreOnly')}
            </span>
          )}
          <div className={styles.fulfillmentOptionHeader}>
            <StorefrontIcon
              width={20}
              height={20}
              className={cn(styles.fulfillmentOptionHeaderIcon, {
                [styles.fullfillmentOptionDisabled]: isDisabled,
              })}
            />
            <span
              className={cn(styles.fulfillmentOptionTitle, {
                [styles.fullfillmentOptionDisabled]: isDisabled,
              })}
            >
              {t('product.free.store.pickup.new')}
            </span>
          </div>
          <div className={styles.fulfillmentOptionFlexContainer}>
            {isClient && !favouriteStoreDetails?.data?.mec_store_id && (
              <SelectStoreMessage />
            )}

            {isClient &&
              favouriteStoreDetails?.data?.mec_store_id &&
              !selectedVariant && (
                <SelectVariantMessage
                  colours={colours?.length}
                  sizes={sizes?.length}
                />
              )}

            {favouriteStoreDetails?.data?.mec_store_id && (
              <StoreAvailabilityMessage
                storeId={favouriteStoreDetails?.data?.mec_store_id}
                selectedAvailability={selectedAvailability}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductFulfillment;
